<script setup lang="ts">
import { HgLayout, HgMainNavItem, HgSideMenuNavItem, MenuItem } from '@smg-real-estate/domus';
import { useHead } from '@unhead/vue';
import { computed, onMounted, onServerPrefetch, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import FavouritesLink from '@/components/common/FavouritesLink/FavouritesLink.vue';
import { useAuth0Client } from '@/composables/useAuth0Client';
import useConditionalSSRContext from '@/composables/useConditionalSSRContext';
import { useRouting } from '@/composables/useRouting';
import { checkUserAuthenticated } from '@/helpers/Auth/authClient';
import { getAppMetaInfo } from '@/helpers/Meta/metaInfoHelper';
import { getPage, isFunnelPage } from '@/helpers/Route/routeHelper';
import { setTemporaryUUIDCookie } from '@/store/modules/split/services';
import { defaultSmgSplitSession } from '@/store/modules/split/store';
import { getHowSourceFromQuery } from '@/tracking/howPropertiesTracking';
import { useAppTracking } from '@/tracking/tracking';
import { initSplitStoreFromSession } from '@smg-real-estate/vue-experiments';
import {
  getAndroidWebViewParamsFromStorage,
  setAndroidWebViewParamsInStorage,
} from './helpers/SessionStorage/sessionStorageHelper';
import { SplitFeatureFlags } from './store/modules/split/splitFeatureFlags';

// Composables
const { tm, locale } = useI18n();
const route = useRoute();
const store = useStore();
const auth0 = useAuth0Client();

// Context and reactive data
const ctx = useConditionalSSRContext();
const smgSplitSession = computed(() => ctx?.smgSplitSession ?? defaultSmgSplitSession);
const baseUrl = import.meta.env.VUE_APP_BASE_URL as string;
const accountBaseUrl = import.meta.env.VUE_APP_ACCOUNT_VIEW_BASE_URL as string;
const source = route.query?.source as string;
const tenantPlusCh = route.query?.tenantPlusCh as string;
const isLoggedIn = ref(false);
const { getLanguageSwitchLink } = useRouting();
const {
  handleMainNavTracking,
  handleMobileMenuNavTracking,
  handleTrackFooterClick,
  handleTrackHeaderClick,
} = useAppTracking(locale);
const ANDROID_WEBVIEW_SOURCE = 'ANDROID_WEBVIEW';

// Computed properties (from Vuex getters)
const splitSessionKey = computed(() => store.getters['split/getSplitSessionKey']);
const isNewNavigationEnabled = computed(() =>
  store.getters['split/isTreatmentOn'](SplitFeatureFlags.DD4533_NEW_TOP_LEVEL_NAVIGATION),
);

// Computed properties (component logic)
const adMenuItem = computed((): MenuItem => {
  return tm(
    isNewNavigationEnabled.value
      ? '$hg.nav.topNavNewListingMenuItem'
      : '$hg.nav.harmonize.newListingMenuItem',
  ) as unknown as MenuItem;
});

const userAccountMenuItem = computed((): MenuItem => {
  return {
    label: tm('$hg.nav.harmonize.overviewMenuItem.label'),
    href: `${accountBaseUrl}/${locale.value}/overview`,
  };
});

const mainHeaderMenuItem = computed((): MenuItem => {
  if (isLoggedIn.value) {
    let privateItems = JSON.parse(JSON.stringify(tm('$hg.nav.privateItems')));
    if (Array.isArray(privateItems)) {
      privateItems = privateItems.map((item) => {
        let modifiedHref;

        if (
          item.href.includes('messenger') ||
          item.href.includes('my-ads') ||
          item.trackingLabel.includes('searchAlertSettings') ||
          item.href.includes('my-realestate')
        ) {
          modifiedHref = `${baseUrl}${item.href}`;
        } else {
          modifiedHref = `${accountBaseUrl}${item.href}`;
        }

        return { ...item, href: modifiedHref };
      });
    }
    return {
      label: tm('$hg.nav.loggedInMenuItem.label'),
      children: privateItems,
    };
  } else {
    return {
      label: tm('$hg.nav.harmonize.overviewMenuItem.label'),
      href: `${accountBaseUrl}/${locale.value}/overview`,
    };
  }
});

const isFooterHidden = computed((): boolean => {
  return isFunnelPage(route.path);
});

// Head/Meta management
useHead(() => getAppMetaInfo(locale.value as any));

// Methods (Vuex actions)
const updateSplitStoreFromApi = () => {
  store.dispatch('split/updateSplitStoreFromApi');
};

// Methods (Vuex mutations)
const setCurrentPage = (page: string) => {
  store.commit('common/setCurrentPage', page);
};

const setLoggedInUser = (username: string) => {
  store.commit('common/setLoggedInUser', username);
};

const setHowSource = (source: string) => {
  store.commit('common/setHowSource', source);
};

const resetUser = () => {
  store.commit('common/resetUser');
};
const updateUserDetails = async () => {
  if (await checkUserAuthenticated()) {
    isLoggedIn.value = true;
    setLoggedInUser(auth0?.user.value?.['cognito:username'] || '');
  } else {
    isLoggedIn.value = false;
    resetUser();
  }
};

const shouldHideMyTenantPlusMenuItem = computed(() => {
  const storedParams = getAndroidWebViewParamsFromStorage();
  const source = (route.query?.source as string) || storedParams.source;
  const tenantPlusCh = (route.query?.tenantPlusCh as string) || storedParams.tenantPlusCh;

  return source === ANDROID_WEBVIEW_SOURCE && tenantPlusCh !== 'true';
});

const syncHowSourceFromRoute = () => {
  const howSource = getHowSourceFromQuery(route.query);
  if (howSource) {
    setHowSource(howSource);
  }
};

// Watchers
watch(
  () => route.path,
  (path: string) => {
    const currentPage = getPage(path);
    if (currentPage) {
      setCurrentPage(currentPage);
    }
  },
);

watch(
  () => route.query,
  () => {
    syncHowSourceFromRoute();
  },
  { immediate: true },
);

// Lifecycle hooks
onMounted(() => {
  window.addEventListener('optanonHandler', () => {
    setTemporaryUUIDCookie(splitSessionKey.value, document.cookie, window.location.href);
    updateSplitStoreFromApi();
  });
  updateUserDetails();

  if (source || tenantPlusCh) {
    setAndroidWebViewParamsInStorage(source, tenantPlusCh);
  }
});

onServerPrefetch(() => {
  const currentPage = getPage(route.path);
  if (currentPage) {
    setCurrentPage(currentPage);
  }
  initSplitStoreFromSession(store, smgSplitSession.value);
});
</script>

<template>
  <div id="app">
    <HgLayout
      :baseUrl="baseUrl"
      :currentLanguage="$i18n.locale"
      :currentRoute="$route"
      :getLanguageToLink="getLanguageSwitchLink"
      :hideFooter="isFooterHidden"
      :isTopNavEnabled="isNewNavigationEnabled"
      :isUserLoggedIn="isLoggedIn"
      @trackFooterClick="handleTrackFooterClick"
      @trackHeaderClick="handleTrackHeaderClick"
    >
      <template #headerMetaAction>
        <HgMainNavItem
          :menuItem="adMenuItem"
          :currentRoute="$route"
          :baseUrl="baseUrl"
          :bordered="true"
          @click="
            handleTrackHeaderClick({
              eventAction: 'open_insertAd',
              eventLabel: 'tab_navigation',
              contentLanguage: $i18n.locale,
            })
          "
        />
      </template>
      <template #headerMetaNavigation>
        <FavouritesLink
          menuNavItem="HgMainNavItem"
          @click="
            handleTrackHeaderClick({
              eventAction: 'open_favourites',
              eventLabel: 'tab_navigation',
              contentLanguage: $i18n.locale,
            })
          "
        />
        <HgMainNavItem
          :menuItem="mainHeaderMenuItem"
          icon="PersonOutline"
          :baseUrl="baseUrl"
          dropdownOrientation="right"
          :currentRoute="$route"
          @mainNavItemClick="handleMainNavTracking"
          @dropDownMenuItemClick="handleMainNavTracking"
        />
      </template>
      <template #sideMenuMetaNavigation>
        <FavouritesLink
          menuNavItem="HgSideMenuNavItem"
          @click="
            handleTrackHeaderClick({
              eventAction: 'open_favourites',
              eventLabel: 'tab_navigation',
              contentLanguage: $i18n.locale,
            })
          "
        />
        <template v-if="!isLoggedIn">
          <HgSideMenuNavItem
            :menuItem="userAccountMenuItem"
            icon="PersonOutline"
            :baseUrl="baseUrl"
            :currentRoute="$route"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
        </template>
      </template>
      <template #sideMenuAccountNavigation>
        <template v-if="isLoggedIn">
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.overviewMenuItem') as MenuItem"
            icon="PersonOutline"
            :currentRoute="$route"
            :baseUrl="accountBaseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.settingsMenuItem') as MenuItem"
            icon="Settings"
            :currentRoute="$route"
            :baseUrl="accountBaseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.myAdsMenuItem') as MenuItem"
            icon="Appointment"
            :currentRoute="$route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.myRealEstateMenuItem') as MenuItem"
            icon="House"
            :currentRoute="$route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.searchAlertMenuItem') as MenuItem"
            icon="Bell"
            :currentRoute="$route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            v-if="!shouldHideMyTenantPlusMenuItem"
            :menuItem="$tm('$hg.nav.harmonize.tenantPlusMenuItem') as MenuItem"
            icon="PlusMembership"
            :currentRoute="$route"
            :baseUrl="baseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
          <HgSideMenuNavItem
            :menuItem="$tm('$hg.nav.harmonize.signOutMenuItem') as MenuItem"
            icon="SignOut"
            :currentRoute="$route"
            :baseUrl="accountBaseUrl"
            @sideMenuItemClick="handleMobileMenuNavTracking"
          />
        </template>
      </template>
      <input id="utm_source" type="hidden" />
      <input id="utm_medium" type="hidden" />
      <input id="utm_campaign" type="hidden" />
      <router-view />
    </HgLayout>
  </div>
</template>

<style lang="scss">
@use '@/assets/globals-hg' as *;

#side-menu-language-switch a {
  &.router-link-exact-active {
    border-bottom: 0;
  }
}
</style>
